import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  isTokenExpired,
  getTokenAuthorities,
  getTokenUserId,
} from "../utils/tokenUtils";
import { clearAll as clearConsultSuppression } from "../utils/consultSuppression";
import {
  deriveAdvisorPersona,
  deriveAssociationPersona,
  derivePersonaKey,
} from "../utils/sessionPersona";
import { RootState } from "./index";

type userInfoType = {
  id: string;
  username: string;
  firstName: string;
  secondName: string;
  roles: string[];
};

export interface AuthState {
  token: string | null;
  loginError: boolean;
  loadingLogin: boolean;
  loginServerError: boolean;

  userInfo: userInfoType | null;
  loadingUserInfo: boolean;
  userInfoError: boolean;

  marketplaceInfo: any;
  loadingMarketplaceInfo: boolean;
  marketplaceInfoError: boolean;

  loadingForgetPassword: boolean;
  forgetPasswordError: boolean;

  isAdmin: boolean;
  isPharmacist: boolean;
  isDriver: boolean;
  /** ITAM-2887: the association advisor persona (ADVISOR_USER). */
  isAdvisor: boolean;
  /** Association portal: the association administrator persona (ASSOCIATION_USER). */
  isAssociationUser: boolean;

  driverSignature: string | null;
  loadingDriverSignature: boolean;

  authInitialized: boolean;
  /** ITAM-2887: the advisor logout is in its bounded side-effect window
   *  (availability pause, up to 3 s). A call accepted now would be dropped
   *  by the logout that follows, so the incoming-call alert refuses it. */
  loggingOut: boolean;
}

const initialState: AuthState = {
  token: null,
  loginError: false,
  loadingLogin: false,
  loginServerError: false,

  userInfo: {
    id: "",
    username: "",
    firstName: "",
    secondName: "",
    roles: [],
  },
  loadingUserInfo: false,
  userInfoError: false,

  marketplaceInfo: null,
  loadingMarketplaceInfo: false,
  marketplaceInfoError: false,

  loadingForgetPassword: false,
  forgetPasswordError: false,

  isAdmin: false,
  isPharmacist: false,
  isDriver: false,
  isAdvisor: false,
  isAssociationUser: false,

  driverSignature: null,
  loadingDriverSignature: false,

  authInitialized: false,
  loggingOut: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialState,
  reducers: {
    loginStart: (state) => {
      state.loadingLogin = true;
    },
    userInfoFn: (state, action: PayloadAction<userInfoType | null>) => {
      state.userInfo = action.payload;
      state.loadingUserInfo = false;
      state.userInfoError = false;
    },
    fetchUserInfoStart: (state) => {
      state.loadingUserInfo = true;
      state.userInfoError = false;
    },
    fetchUserInfoFailed: (state) => {
      state.loadingUserInfo = false;
      state.loadingLogin = false;
      state.userInfoError = true;
    },

    loginSuccess: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
      state.loadingLogin = false;
      state.loginError = false;
    },
    loginFailed: (state) => {
      state.loadingLogin = false;
      state.loginError = true;
    },
    loginStatusReset: (state) => {
      state.loginError = false;
      state.loadingLogin = false;
      state.loginServerError = false;
    },
    logout: (state) => {
      // setCNCActiveStatus(state.marketplaceInfo.branchMarketplaceId, state.marketplaceInfo.markeplaceId, 0);
      authSlice.actions.userInfoFn(null);
      authSlice.caseReducers.logoutUser(state);
      authSlice.caseReducers.clearDriverSignature(state);
      authActions.marketplaceInfo(null);
      // ITAM-2887: the persona flags must not outlive the session, or a
      // re-login as a different role would keep the previous UI.
      state.isAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isAdvisor = false;
      state.isAssociationUser = false;
      state.loggingOut = false;
    },
    logoutUser: (state) => {
      clearLocalStorage();
      state.token = null;
      state.loginError = false;
      state.loadingLogin = false;
      // Keep authInitialized true so we don't show loading spinner after logout
      state.authInitialized = true;
    },
    fetchMarketplaceInfoStart: (state) => {
      state.loadingMarketplaceInfo = true;
      state.marketplaceInfoError = false;
    },
    marketplaceInfo: (state, action) => {
      state.marketplaceInfo = action.payload;
      state.loadingMarketplaceInfo = false;
      state.marketplaceInfoError = false;
    },
    fetchMarketplaceInfoFailed: (state, action) => {
      state.marketplaceInfoError = action.payload;
      state.loadingMarketplaceInfo = false;
      state.loadingLogin = false;
      state.marketplaceInfoError = true;
    },
    loginServerError: (state) => {
      state.loginError = false;
      state.loginServerError = true;
      state.loadingLogin = false;
    },
    setActiveStatus: (state, action) => {
      state.marketplaceInfo = {
        ...state.marketplaceInfo,
        takesOrders: action.payload,
      };
    },
    setIsAdmin: (state) => {
      state.isAdmin = true;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isAdvisor = false;
      state.isAssociationUser = false;
    },
    setIsPharmacist: (state) => {
      state.isAdmin = false;
      state.isPharmacist = true;
      state.isDriver = false;
      state.isAdvisor = false;
      state.isAssociationUser = false;
    },
    setIsDriver: (state) => {
      state.isDriver = true;
      state.isAdmin = false;
      state.isPharmacist = false;
      state.isAdvisor = false;
      state.isAssociationUser = false;
    },
    setIsAdvisor: (state) => {
      state.isAdvisor = true;
      state.isAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isAssociationUser = false;
    },
    setIsAssociationUser: (state) => {
      state.isAssociationUser = true;
      state.isAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isAdvisor = false;
    },
    setRegularUser: (state) => {
      state.isAdmin = false;
      state.isPharmacist = false;
      state.isDriver = false;
      state.isAdvisor = false;
      state.isAssociationUser = false;
    },
    setDriverSignature: (state, action: PayloadAction<string | null>) => {
      state.driverSignature = action.payload;
      state.loadingDriverSignature = false;
    },
    setLoadingDriverSignature: (state, action: PayloadAction<boolean>) => {
      state.loadingDriverSignature = action.payload;
    },
    clearDriverSignature: (state) => {
      state.driverSignature = null;
      state.loadingDriverSignature = false;
    },
    /**
     * ITAM-2887: a token refresh that changed persona. Dispatched BEFORE the
     * new persona's flags are set; the consult slices listen for the action
     * type "auth/personaExit" and reset to their initial state, because a
     * refresh dispatches `loginSuccess`, never `auth/logout`. Marketplace
     * state is cleared too: after a persona change the old pair is stale.
     */
    personaExit: (state) => {
      state.marketplaceInfo = null;
      localStorage.removeItem("marketplaceId");
      localStorage.removeItem("branchMarketplaceId");
    },
    setAuthInitialized: (state, action: PayloadAction<boolean>) => {
      state.authInitialized = action.payload;
    },
    logoutStarted: (state) => {
      state.loggingOut = true;
    },
  },
});

const clearLocalStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("expirationDate");
  localStorage.removeItem("email");
  localStorage.removeItem("id");
  localStorage.removeItem("firstName");
  localStorage.removeItem("secondName");
  localStorage.removeItem("roles");
  localStorage.removeItem("marketplaceId");
  localStorage.removeItem("branchMarketplaceId");
  localStorage.removeItem("driverSignature");

  // Video consult: drop the joined-consult suppression markers. Must be
  // explicit; the markers are keyed per consult and would otherwise survive
  // into the next session on this browser.
  clearConsultSuppression();
};

export const authActions = authSlice.actions;

// Memoized selector for checking if user is authenticated with valid token
export const selectIsAuthenticated = createSelector(
  [
    (state: RootState) => state.auth.token,
    (state: RootState) => state.auth.authInitialized,
  ],
  (token, isInitialized) => {
    if (!token || !isInitialized) {
      return false;
    }
    try {
      return !isTokenExpired(token);
    } catch {
      console.warn("Auth: Invalid token format during validation");
      return false;
    }
  },
);

// Extract user roles from the token using tokenUtils
export const selectUserRoles = createSelector(
  [selectIsAuthenticated, (state: RootState) => state.auth.token],
  (isAuthenticated, token) => {
    if (!isAuthenticated || !token) {
      return [];
    }
    return getTokenAuthorities(token);
  },
);

// ITAM-2887 role predicates. All of them delegate to the one pure helper, so
// the auth thunks (which only have a raw role array at branch time) and these
// selectors can never disagree about who is an advisor.

/**
 * Gates EVERY consult surface, never the raw role. An `ADMIN + ADVISOR_USER`
 * token keeps the admin persona with no advisor side effects.
 */
export const selectHasSupportedAdvisorCapability = createSelector(
  [selectUserRoles],
  (roles) => deriveAdvisorPersona(roles).hasSupportedAdvisorCapability,
);

export const selectIsAdvisorSession = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isAdvisor],
  (roles, flag) => flag || deriveAdvisorPersona(roles).hasSupportedAdvisorCapability,
);

// Association portal predicates, same construction as the advisor's.
export const selectHasSupportedAssociationCapability = createSelector(
  [selectUserRoles],
  (roles) => deriveAssociationPersona(roles).hasSupportedAssociationCapability,
);

export const selectIsAssociationSession = createSelector(
  [selectUserRoles, (state: RootState) => state.auth.isAssociationUser],
  (roles, flag) =>
    flag || deriveAssociationPersona(roles).hasSupportedAssociationCapability,
);

/**
 * The dobrocudan.rs portal personas (advisor, association): no GPS, no
 * device location prompt, no CnC header controls, drawer navigation.
 */
export const selectIsPortalPersona = createSelector(
  [selectIsAdvisorSession, selectIsAssociationSession],
  (advisor, association) => advisor || association,
);

/**
 * Scalar remount key for `ConsultOrderManager`. Must stay a scalar:
 * interpolating the persona OBJECT yields "42:[object Object]", a key that
 * never changes and silently defeats the teardown.
 */
export const selectPersonaKey = createSelector([selectUserRoles], (roles) =>
  derivePersonaKey(roles),
);

export const selectTokenUserId = createSelector(
  [selectIsAuthenticated, (state: RootState) => state.auth.token],
  (isAuthenticated, token) =>
    isAuthenticated && token ? getTokenUserId(token) : null,
);

export default authSlice;
